import { useTranslation } from 'react-i18next';
import { PlaceholderContainer } from '@topcon/topcon-utils';
import { use, useState } from 'react';
import { TeContainer, TeContainerSlim, TeDivider, TeText } from '@topcon/tuicl-react';

import { ServiceAgreementTermsEnum } from '../../api/generated/pm';
import { useServiceAgreementWithId } from '../../pages/Customers/customers.queries';
import { useAppData } from '../../hooks/queries/appSession.queries';
import { ServiceAgreementStepActions } from './ServiceAgreementStepActions';
import { ReviewResultContext } from '../../providers/ReviewResultContext/review-result.context';
import { ReviewResultContextType } from '../../providers/ReviewResultContext/review-result.types';
import { AgreementType } from '../../types/customer-modal.types';
import SelectableAgreementTypeCard from './SelectableAgreementTypeCard';
import ServiceAgreementAcknowledgements from './ServiceAgreementAcknowledgements';

interface ServiceAgreementStepProps {
  id: string;
  visible: boolean;
  onNextStep: () => void;
  onPreviousStep?: () => void;
}

function ReviewAgreementTypeStep({
  visible,
  onNextStep,
  onPreviousStep,
  id,
}: ServiceAgreementStepProps) {
  const { t } = useTranslation();
  const { data: serviceAgreementData, isLoading: isServiceAgreementLoading } =
    useServiceAgreementWithId(id);
  const { isDealer } = useAppData();
  const { setResult } = use<ReviewResultContextType>(ReviewResultContext);
  const [acknowledged, setAcknowledged] = useState<boolean>(false);

  if (!visible) return;

  if (isServiceAgreementLoading) {
    return <PlaceholderContainer hasLoader text={t('loading', { ns: 'settings' })} />;
  }

  const handleReject = () => {
    setResult('declined');
  };

  return (
    <TeContainer justifyContent="space-between" fill padding={0} gap={0} column>
      <TeContainerSlim column fill padding={['m']} gap="xs2">
        <TeText weight={700} size={20}>
          {t('serviceAgreement.agreementReview.title')}
        </TeText>
        <TeText color="text.secondary" size={14}>
          {t('serviceAgreement.agreementReview.description')}
        </TeText>
      </TeContainerSlim>
      <TeDivider dividerColor="border.secondary" />
      <TeContainer grow>
        <SelectableAgreementTypeCard
          title={t('serviceAgreement.full_service')}
          agreementType={AgreementType.FullService}
          isActive={
            serviceAgreementData?.serviceAgreementTerms === ServiceAgreementTermsEnum.FULL_SERVICE
          }
          disabled={
            serviceAgreementData?.serviceAgreementTerms !== ServiceAgreementTermsEnum.FULL_SERVICE
          }
          badgeLabel={t('requested')}
        />
        <SelectableAgreementTypeCard
          title={t('serviceAgreement.limited_service')}
          agreementType={AgreementType.LimitedService}
          isActive={
            serviceAgreementData?.serviceAgreementTerms ===
            ServiceAgreementTermsEnum.PARTIAL_SERVICE
          }
          disabled={
            serviceAgreementData?.serviceAgreementTerms !==
            ServiceAgreementTermsEnum.PARTIAL_SERVICE
          }
          badgeLabel={t('requested')}
        />
      </TeContainer>
      <TeDivider dividerColor="border.secondary" />
      <ServiceAgreementAcknowledgements
        side={isDealer ? 'dealer' : 'customer'}
        tier={serviceAgreementData?.serviceAgreementTerms}
        onAcknowledged={setAcknowledged}
      />
      <TeDivider dividerColor="border.secondary" />
      <ServiceAgreementStepActions
        step="agreementReview"
        id={id}
        onNextStep={onNextStep}
        onPreviousStep={onPreviousStep}
        onReject={handleReject}
        isNextStepDisabled={!acknowledged}
      />
    </TeContainer>
  );
}

export default ReviewAgreementTypeStep;
